'use client';

import { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import Image from 'next/image';

interface TiltedCardProps {
  imageSrc: string;
  width?: string;
  height?: string;
  scaleOnHover?: number;
  rotateAmplitude?: number;
  overlayContent?: React.ReactNode;
  displayOverlayContent?: boolean;
}

export default function TiltedCard({
  imageSrc,
  width = '600px',
  height = '300px',
  scaleOnHover = 1.1,
  rotateAmplitude = 14,
  overlayContent = null,
  displayOverlayContent = false,
}: TiltedCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Only run GSAP tilt logic if NOT mobile
  useEffect(() => {
    if (isMobile) return;

    const card = cardRef.current;
    const inner = innerRef.current;
    const caption = captionRef.current;
    if (!card || !inner) return;

    let lastY = 0;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const offsetX = e.clientX - rect.left - rect.width / 2;
      const offsetY = e.clientY - rect.top - rect.height / 2;

      const rotationX = (-offsetY / (rect.height / 2)) * rotateAmplitude;
      const rotationY = (offsetX / (rect.width / 2)) * rotateAmplitude;

      gsap.to(inner, {
        rotateX: rotationX,
        rotateY: rotationY,
        transformPerspective: 800,
        transformOrigin: 'center',
        ease: 'power3.out',
        duration: 0.3,
      });

      if (caption) {
        const velocityY = offsetY - lastY;
        gsap.to(caption, {
          rotate: -velocityY * 0.6,
          x: offsetX + rect.width / 2,
          y: offsetY + rect.height / 2,
          opacity: 1,
          duration: 0.3,
          ease: 'power3.out',
        });
      }

      lastY = offsetY;
    };

    const handleMouseEnter = () => {
      gsap.to(inner, { scale: scaleOnHover, duration: 0.3, ease: 'power3.out' });
      if (caption) gsap.to(caption, { opacity: 1, duration: 0.3 });
    };

    const handleMouseLeave = () => {
      gsap.to(inner, {
        rotateX: 0,
        rotateY: 0,
        scale: 1,
        duration: 0.5,
        ease: 'elastic.out(1, 0.5)',
      });
      if (caption)
        gsap.to(caption, {
          opacity: 0,
          rotate: 0,
          x: 0,
          y: 0,
          duration: 0.3,
        });
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseenter', handleMouseEnter);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseenter', handleMouseEnter);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isMobile, scaleOnHover, rotateAmplitude]);

  // ✅ If mobile, render plain image only
  if (isMobile) {
    return (
      <Image
        src={imageSrc}
        alt={imageSrc}
        width={parseInt(width)}
        height={parseInt(height)}
        priority
        fetchPriority="high"
        className="w-full h-auto rounded-[15px] object-contain"
      />
    );
  }

  // ✅ Otherwise render TiltedCard
  return (
    <div
      ref={cardRef}
      className="relative [perspective:1000px] flex items-center justify-center group"
      style={{ width, height }}
    >
      <div
        ref={innerRef}
        className="relative w-full h-full rounded-[15px] [transform-style:preserve-3d]"
      >
        <Image
          src={imageSrc}
          alt={imageSrc}
          width={parseInt(width)}
          height={parseInt(height)}
          priority
          fetchPriority="high"
          className="w-full h-auto rounded-[15px] object-contain will-change-transform"
        />

        {displayOverlayContent && overlayContent && (
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100
            flex items-center justify-center text-white text-lg font-bold 
            [transform:translateZ(40px)] pointer-events-none
            transition-all duration-500 ease-out"
          >
            {overlayContent}
          </div>
        )}
      </div>
    </div>
  );
}
